import { useMemo } from 'react';
import useSWR from 'swr';
import FrontendHttpClient from '@/utils/http-handler/frontend-http-client';
import NotificationService from '@/components/notification-service';
import { route } from 'nextjs-routes';

const CERT_COUNT_URL = route({
  pathname: '/api/v2/holder_addresses/affected-certificate-count',
});

const fetchCertificateData = async (url, holderAddressIds) => {
  const { data } = await FrontendHttpClient.post(url, { holderAddressIds });
  return data;
};

/**
 * Custom hook to fetch certificate count for holder addresses using SWR.
 *
 * When `allAddressIds` is provided, all addresses are fetched upfront so that
 * switching `selectedHolderAddressIds` does not trigger additional requests.
 *
 * @param {string[]} selectedHolderAddressIds - Address IDs to sum the count for
 * @param {string[]} allAddressIds - If provided, prefetch counts for all addresses
 * @param {Object} options - SWR configuration options
 * @returns {{ count: number, addressCounts: Object, isLoading: boolean, error: any, mutate: function }}
 */
export const useHolderAddressCertCount = ({
  selectedHolderAddressIds = [],
  allAddressIds = [],
  options = {},
}) => {
  const fetchIds =
    allAddressIds.length > 0 ? allAddressIds : selectedHolderAddressIds;
  const swrKey = fetchIds.length > 0 ? [CERT_COUNT_URL, fetchIds] : null;

  const { data, error, isLoading, mutate } = useSWR(
    swrKey,
    ([url, addressIds]) => fetchCertificateData(url, addressIds),
    {
      onError: err =>
        NotificationService.error(`Error fetching holder address info ${err}`),
      revalidateOnFocus: false,
      revalidateOnReconnect: true,
      dedupingInterval: 5000,
      ...options,
    }
  );

  const addressCounts = data?.addressCounts ?? {};

  const count = useMemo(() => {
    if (!data) {
      return 0;
    }

    if (data.addressCounts) {
      return selectedHolderAddressIds.reduce(
        (total, id) => total + (data.addressCounts[id] ?? 0),
        0
      );
    }

    return data.count ?? 0;
  }, [data, selectedHolderAddressIds]);

  return { count, addressCounts, isLoading, error, mutate };
};
